import {makeAutoObservable} from 'mobx';
import {recording} from '../../proto/compiled';

export class VideoPlayerState {
  playing = false;
  currentTimeMs = 0;
  durationMs = 0;
  volume = 1;
  muted = false;
  hoveredAnnotation?: recording.IRecordingAnnotation;
  hoveredMarkerType?: 'start' | 'end';
  isDraggingTimeline = false;
  isDraggingHandle = false;
  draggingHandleType?: 'start' | 'end';
  draggingHandlePosition = 0;
  wasDraggingHandle = false;

  constructor() {
    makeAutoObservable(this);
  }

  setPlaying(playing: boolean) {
    this.playing = playing;
  }

  setCurrentTime(ms: number) {
    this.currentTimeMs = Math.max(0, Math.round(ms));
  }

  setDuration(ms: number) {
    this.durationMs = Math.max(0, Math.round(ms));
  }

  setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  setMuted(muted: boolean) {
    this.muted = muted;
  }

  toggleMuted() {
    this.muted = !this.muted;
  }

  setHoveredAnnotation(annotation?: recording.IRecordingAnnotation, markerType?: 'start' | 'end') {
    this.hoveredAnnotation = annotation;
    this.hoveredMarkerType = markerType;
  }

  setDraggingTimeline(dragging: boolean) {
    this.isDraggingTimeline = dragging;
  }

  setDraggingHandle(dragging: boolean, type?: 'start' | 'end') {
    if (!dragging && this.isDraggingHandle) {
      // Mark that we just finished dragging (to prevent click from triggering play)
      this.wasDraggingHandle = true;
    }
    this.isDraggingHandle = dragging;
    this.draggingHandleType = dragging ? type : undefined;
    if (!dragging) {
      this.draggingHandlePosition = 0;
    }
  }

  clearWasDragging() {
    this.wasDraggingHandle = false;
  }

  setDraggingHandlePosition(position: number) {
    this.draggingHandlePosition = position;
  }

  get progressPercent(): number {
    if (this.durationMs <= 0) return 0;
    return Math.min(100, Math.max(0, (this.currentTimeMs / this.durationMs) * 100));
  }
}

export const formatTime = (ms: number): string => {
  if (ms < 0 || !isFinite(ms)) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const getTimePosition = (timeMs: number, durationMs: number): number => {
  if (durationMs <= 0) return 0;
  return Math.min(100, Math.max(0, (timeMs / durationMs) * 100));
};

export const getTimeFromPosition = (percent: number, durationMs: number): number => {
  return Math.round((percent / 100) * durationMs);
};
